import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { AssessmentReportMWOptions } from '@utils/Variables'
import { useDropdownMenu } from '@utils/assessmentReportUtils'
import { enumToDrpOpts } from '@utils/enumHelper'
import { Flex } from 'antd'
import { useMemo, useState } from 'react'

import { Database_Procs_TeamBonusGames_Item } from '@suntex.fim/api-client'
import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { ExportMenu, LoaderOverlay, SearchInput } from '@suntex.fim/ui'

import columns, {
  hidableSelectLabel
} from './AssessmentReport/AssessmentReportMWTable'
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

/**
 * returns JSX for measurement world report
 */
function AssessmentReportMW() {
  const { api } = useFramework()
  const team = useOptionalTeamInfo()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  const [isExporting, setIsExporting] = useState(false)

  const { data: assessmentReport = [], isLoading } = useQuery({
    queryKey: ['assessment', 'bonusTeam', team?.id],
    queryFn: async () => {
      const response = await api.get_Assessment_BonusTeam({
        teamId: team!.id
      })
      return response.teamBonusGames || []
    },
    enabled: team?.id != null
  })
  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }
  const memoColumns = useMemo<ColumnType<Database_Procs_TeamBonusGames_Item>[]>(
    () => columns({ graphColor: '#3FB2FF', sendMessage }),
    []
  )
  const linkOptions = enumToDrpOpts(AssessmentReportMWOptions).map((key) => ({
    key: `word-problems/${key.label}`,
    label: key.label
  }))
  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } =
    useAssessmentTable<Database_Procs_TeamBonusGames_Item>({
      data: assessmentReport,
      columns: memoColumns,
      isLoading,
      globalFilter: searchQuery,
      initialSorting: [{ id: 'displayName', desc: false }],
      horizontalScroll: true
    })

  const selectedRow = table.getSelectedRowModel().flatRows
  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: Database_Procs_TeamBonusGames_Item }) => ({
      label: original.displayName || original.userId,
      value: original.playerId
    })
  )
  const items = useDropdownMenu({
    table,
    hidableSelectLabel,
    fileName: `${team?.name}_MW`,
    setIsLoading: setIsExporting
  })
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay isLoading={isLoading || isExporting} />
        <AssessmentReportNavigation
          exportButton={
            <ExportMenu
              ariaLabel="Export report options"
              items={items}
              trigger={['click']}
            />
          }
        />
        <Flex className="tiles-container" wrap="wrap">
          <Flex className="left" vertical gap="20px" flex="1 1 560px">
            <AssessmentReportSummaryDashboard />
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">Team Players</h4>

            <Flex
              gap="10px"
              justify="space-between"
              wrap
              className="print-hidden"
            >
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              <Button
                intent="tertiary"
                size="small"
                disabled={isDisabled}
                onClick={() => setIsModalOpen(true)}
              >
                SEND MESSAGE
              </Button>
            </Flex>
          </Flex>
          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          linkOptions={linkOptions}
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
          reportType="Measurement World"
        />
      </Flex>
    </>
  )
}
export default AssessmentReportMW
